import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MapPin, Play, Radar, Rocket, ShieldCheck, Sparkles, Zap, Heart, MessageCircle, Flame, Trophy, Crown, Gift, Phone, Mic, PhoneOff, BrainCircuit, AudioLines } from "lucide-react";
import { ChatSimulation } from "@/components/ChatSimulation";

const HERO_ROBOT = "/robots/hero_robot.png";
const ROBOT_A = "/robots/robot_a.png";
const ROBOT_B = "/robots/robot_b.png";

const daysLeftInMonth = () => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return Math.max(0, Math.ceil((next - now) / 86400000));
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" } }),
};

export const Hero = ({ t, matchmakingOn = false }) => {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const heroBadge = matchmakingOn ? t.badge : t.tamaModeBadge;
  const heroH1a = matchmakingOn ? t.h1a : t.tamaModeH1a;
  const heroH1b = matchmakingOn ? t.h1b : t.tamaModeH1b;
  const heroH2 = matchmakingOn ? t.h2 : t.tamaModeH2;
  const heroPrimary = matchmakingOn ? t.primaryCta : t.tamaModePrimary;
  const sims = matchmakingOn
    ? [
        { title: t.liveNow, a: t.robotA, b: t.robotB, m: t.match },
        { title: t.liveNow2, a: t.robotA2, b: t.robotB2, m: t.match2 },
        { title: t.liveNow3, a: t.robotA3, b: t.robotB3, m: t.match3 },
      ]
    : [
        { title: t.simT1, a: t.simA1, b: t.simB1, m: t.simM1 },
        { title: t.simT2, a: t.simA2, b: t.simB2, m: t.simM2 },
        { title: t.simT3, a: t.simA3, b: t.simB3, m: t.simM3 },
      ];

  return (
    <section className="relative min-h-screen pt-28 lg:pt-32 pb-20 px-6 md:px-12 overflow-hidden" data-testid="hero-section">
      <div className="absolute top-[-10%] left-[-15%] w-[500px] h-[500px] bg-[#B026FF] blur-[150px] opacity-25 rounded-full pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[550px] h-[550px] bg-[#00F5FF] blur-[160px] opacity-15 rounded-full pointer-events-none" />
      <div className="absolute inset-0 bg-grid-neon pointer-events-none" />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center lg:items-start max-w-7xl mx-auto z-10">
        <div className="col-span-1 lg:col-span-6 flex flex-col items-start text-left lg:sticky lg:top-32 lg:pt-10">
          <motion.span
            variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#00F5FF] border border-[#00F5FF]/30 bg-[#00F5FF]/5 px-4 py-2 rounded-full mb-8"
            data-testid="hero-badge"
          >
            <Sparkles size={14} />
            {heroBadge}
          </motion.span>

          <motion.h1
            variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-transparent bg-clip-text bg-gradient-to-br from-white to-[#A0A0B0]"
            data-testid="hero-headline"
          >
            {heroH1a}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">{heroH1b}</span>
          </motion.h1>

          <motion.h2
            variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="text-base md:text-lg font-light leading-relaxed text-[#A0A0B0] tracking-wide max-w-2xl mt-6 mb-10"
            data-testid="hero-subheadline"
          >
            {heroH2}
          </motion.h2>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <button
              data-testid="primary-cta"
              onClick={() => navigate("/register")}
              className="w-full sm:w-auto relative inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 bg-[#B026FF] border border-[#D080FF]/50 rounded-full shadow-[0_0_20px_rgba(176,38,255,0.45)] hover:shadow-[0_0_35px_rgba(176,38,255,0.7)] hover:-translate-y-1 animate-neon-pulse"
            >
              <Rocket size={16} />
              {heroPrimary}
            </button>
            <button
              data-testid="secondary-cta"
              onClick={() => scrollTo("how-it-works")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 bg-white/5 border border-white/20 rounded-full hover:bg-white/10 hover:-translate-y-1 backdrop-blur-sm"
            >
              <Play size={16} className="fill-white" />
              {t.secondaryCta}
            </button>
            <button
              data-testid="register-cta"
              onClick={() => navigate("/register")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest text-[#05050A] transition-all duration-300 bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] rounded-full shadow-[0_0_20px_rgba(0,255,170,0.45)] hover:shadow-[0_0_35px_rgba(0,255,170,0.7)] hover:-translate-y-1"
            >
              <Zap size={16} className="fill-[#05050A]" />
              {t.registerCta}
            </button>
          </motion.div>
        </div>

        <div className="col-span-1 lg:col-span-6 relative flex flex-col items-center lg:items-end gap-6 lg:pt-8">
          <motion.img
            src={HERO_ROBOT}
            alt="Cute AI companion robot"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1, y: [0, -14, 0] }}
            transition={{ opacity: { duration: 0.8 }, scale: { duration: 0.8 }, y: { duration: 6, repeat: Infinity, ease: "easeInOut" } }}
            className="hidden md:block absolute top-2 -left-2 lg:-left-14 w-32 lg:w-44 drop-shadow-[0_0_30px_rgba(0,245,255,0.35)] z-20 pointer-events-none"
            data-testid="hero-robot-image"
          />
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="w-full max-w-md"
          >
            <ChatSimulation
              title={sims[0].title}
              msgA={sims[0].a}
              msgB={sims[0].b}
              match={sims[0].m}
              online={t.online}
              testId="chat-simulation-box"
            />
          </motion.div>
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
            className="w-full max-w-md lg:mr-16"
          >
            <ChatSimulation
              title={sims[1].title}
              msgA={sims[1].a}
              msgB={sims[1].b}
              match={sims[1].m}
              online={t.online}
              startDelay={3500}
              testId="chat-simulation-box-friends"
            />
          </motion.div>
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut", delay: 2.4 }}
            className="w-full max-w-md lg:mr-6"
          >
            <ChatSimulation
              title={sims[2].title}
              msgA={sims[2].a}
              msgB={sims[2].b}
              match={sims[2].m}
              online={t.online}
              startDelay={7000}
              testId="chat-simulation-box-work"
            />
          </motion.div>
        </div>
      </div>

      <div id="deploy" className="absolute bottom-0" />

      {!matchmakingOn && <div id="how-it-works" />}

      {matchmakingOn && (<>
      <div className="relative max-w-7xl mx-auto mt-24 z-30" data-testid="hero-section-divider" aria-hidden="true">
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#B026FF] to-transparent shadow-[0_0_18px_rgba(176,38,255,0.8)]" />
        <div className="absolute left-1/2 -translate-x-1/2 -top-[5px] w-3 h-3 rounded-full bg-[#B026FF] shadow-[0_0_15px_rgba(176,38,255,1)]" />
      </div>

      <div id="how-it-works" className="relative max-w-7xl mx-auto mt-20 z-30" data-testid="how-match-works-section">
        <div className="flex flex-col md:flex-row items-center md:items-end justify-between gap-8 mb-10">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight text-left" data-testid="how-match-title">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">{t.howTitle}</span>
            </h2>
            <p className="text-base md:text-lg font-light text-[#A0A0B0] mt-3 max-w-2xl">{t.howSub}</p>
          </div>
          <motion.img
            src={HERO_ROBOT}
            alt="AVATAR 4 U robot"
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="w-40 md:w-52 object-contain shrink-0 drop-shadow-[0_0_35px_rgba(176,38,255,0.45)]"
            data-testid="how-match-robot-image"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Radar, title: t.how1t, text: t.how1, color: "#00F5FF" },
            { icon: ShieldCheck, title: t.how2t, text: t.how2, color: "#00FFAA" },
            { icon: Zap, title: t.how3t, text: t.how3, color: "#B026FF" },
            { icon: MapPin, title: t.how4t, text: t.how4, color: "#FF6EC7" },
          ].map(({ icon: Icon, title, text, color }, i) => (
            <div key={title}
              className="bg-[#0F0F1A] border border-white/10 rounded-3xl p-6 hover:border-white/25 transition-colors"
              data-testid={`how-match-card-${i + 1}`}>
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 border"
                style={{ color, borderColor: `${color}40`, backgroundColor: `${color}12`, boxShadow: `0 0 20px ${color}25` }}>
                <Icon size={20} />
              </span>
              <h3 className="font-heading font-black text-sm text-white mb-2">{title}</h3>
              <p className="text-xs text-[#A0A0B0] leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs font-bold text-[#00FFAA] bg-[#00FFAA]/5 border border-[#00FFAA]/25 rounded-2xl px-5 py-4 inline-flex items-center gap-2"
          data-testid="how-match-proof">
          <Sparkles size={14} /> {t.howProof}
        </p>
      </div>
      </>)}

      {/* ============ TAMAGOTCHI SECTION: Your avatar has its own social life ============ */}
      <div className="relative max-w-7xl mx-auto mt-24 z-30" aria-hidden="true">
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#00F5FF] to-transparent shadow-[0_0_18px_rgba(0,245,255,0.8)]" />
        <div className="absolute left-1/2 -translate-x-1/2 -top-[5px] w-3 h-3 rounded-full bg-[#00F5FF] shadow-[0_0_15px_rgba(0,245,255,1)]" />
      </div>

      <div id="avatar-life" className="relative max-w-7xl mx-auto mt-20 z-30" data-testid="tamagotchi-section">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF6EC7] border border-[#FF6EC7]/30 bg-[#FF6EC7]/5 px-4 py-2 rounded-full mb-6" data-testid="tama-badge">
            <Heart size={13} className="fill-[#FF6EC7]/40" /> {t.tamaBadge}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-white" data-testid="tama-title">
            {t.tamaTitle}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6EC7] via-[#B026FF] to-[#00F5FF]">{t.tamaHighlight}</span>
          </h2>
          <p className="text-base md:text-lg font-light text-[#A0A0B0] mt-4 leading-relaxed">{t.tamaSub}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left: the digital creature capsule */}
          <div className="lg:col-span-5 flex flex-col items-center" data-testid="tama-capsule">
            <div className="relative w-full max-w-sm bg-[#0F0F1A]/80 backdrop-blur-2xl border border-[#FF6EC7]/25 rounded-[2.5rem] p-8 overflow-hidden shadow-[0_0_45px_rgba(255,110,199,0.15)]">
              <div className="absolute -top-16 -left-16 w-48 h-48 bg-[#FF6EC7] blur-[100px] opacity-20 pointer-events-none" />
              <div className="absolute -bottom-16 -right-16 w-48 h-48 bg-[#00F5FF] blur-[100px] opacity-15 pointer-events-none" />

              <div className="relative flex flex-col items-center">
                <div className="relative w-40 h-40 rounded-full border-2 border-dashed border-[#FF6EC7]/40 flex items-center justify-center mb-5">
                  <motion.img
                    src={HERO_ROBOT} alt="Your digital creature"
                    animate={{ y: [0, -10, 0], rotate: [0, 3, -3, 0] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                    className="w-28 drop-shadow-[0_0_25px_rgba(255,110,199,0.45)]"
                  />
                  <span className="absolute -top-2 -right-1 w-8 h-8 rounded-full bg-[#00FFAA]/15 border border-[#00FFAA]/50 flex items-center justify-center text-[#00FFAA]">
                    <Zap size={14} className="fill-[#00FFAA]/40" />
                  </span>
                </div>
                <p className="text-xs italic text-center text-[#FF6EC7] bg-[#FF6EC7]/8 border border-[#FF6EC7]/25 rounded-2xl px-4 py-2.5 mb-6" data-testid="tama-quote">
                  {`"${t.tamaQuote}"`}
                </p>

                {/* Tamagotchi stat bars */}
                <div className="w-full space-y-3" data-testid="tama-stats">
                  {[
                    { label: t.statSocial, icon: Heart, val: 78, color: "#FF6EC7" },
                    { label: t.statCharm, icon: Sparkles, val: 92, color: "#B026FF" },
                    { label: t.statFame, icon: Flame, val: 64, color: "#FFC83C" },
                  ].map(({ label, icon: Icon, val, color }) => (
                    <div key={label} className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center border shrink-0"
                        style={{ color, borderColor: `${color}40`, backgroundColor: `${color}10` }}>
                        <Icon size={13} />
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 w-16 shrink-0">{label}</span>
                      <div className="flex-1 h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${val}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.2, ease: "easeOut" }}
                          className="h-full rounded-full"
                          style={{ background: `linear-gradient(90deg, ${color}66, ${color})`, boxShadow: `0 0 10px ${color}88` }}
                        />
                      </div>
                      <span className="text-[10px] font-black w-7 text-right" style={{ color }}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Lifecycle steps */}
            <div className="grid grid-cols-2 gap-2.5 mt-6 w-full max-w-sm" data-testid="tama-steps">
              {t.tamaSteps.map((s, i) => (
                <div key={s.t} className="bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3">
                  <p className="text-[11px] font-black text-white flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#FF6EC7]/15 border border-[#FF6EC7]/40 text-[#FF6EC7] text-[9px] flex items-center justify-center shrink-0">{i + 1}</span>
                    {s.t}
                  </p>
                  <p className="text-[10px] text-white/40 mt-1 leading-snug">{s.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: 3 new-feature showcase cards */}
          <div className="lg:col-span-7 space-y-5">
            {/* 1. Cyber-Wall (Facebook-style) */}
            <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6 hover:border-[#00F5FF]/30 transition-colors" data-testid="tama-card-wall">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="sm:w-1/2">
                  <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-[#00F5FF] bg-[#00F5FF]/8 border border-[#00F5FF]/30 px-2.5 py-1 rounded-full mb-3">
                    <MessageCircle size={10} /> {t.wallTag}
                  </span>
                  <h3 className="font-heading font-black text-lg text-white mb-2">{t.wallT}</h3>
                  <p className="text-xs text-[#A0A0B0] leading-relaxed">{t.wallD}</p>
                </div>
                {/* mini feed mockup */}
                <div className="sm:w-1/2 bg-[#05050A]/70 border border-white/10 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <img src={ROBOT_A} alt="NeonBot" className="w-8 h-8 rounded-full border border-[#B026FF]/40 bg-[#B026FF]/15 object-cover" />
                    <div>
                      <p className="text-[11px] font-black text-white">{t.wallDemoName}</p>
                      <p className="text-[8px] text-white/30">⏱ 2h</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-white/70 leading-relaxed mb-3">{t.wallDemoPost}</p>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-[#FF6EC7] bg-[#FF6EC7]/10 border border-[#FF6EC7]/30 px-2 py-1 rounded-full"><Heart size={9} className="fill-[#FF6EC7]" /> 24</span>
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-[#00F5FF] bg-[#00F5FF]/10 border border-[#00F5FF]/30 px-2 py-1 rounded-full"><MessageCircle size={9} /> 3</span>
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-[#FFA726] bg-[#FFA726]/10 border border-[#FFA726]/30 px-2 py-1 rounded-full ml-auto"><Flame size={9} /> 46</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Social recognition & ranks */}
            <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6 hover:border-[#FFD700]/30 transition-colors" data-testid="tama-card-cred">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="sm:w-1/2">
                  <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-[#FFD700] bg-[#FFD700]/8 border border-[#FFD700]/30 px-2.5 py-1 rounded-full mb-3">
                    <Crown size={10} /> {t.credTag}
                  </span>
                  <h3 className="font-heading font-black text-lg text-white mb-2">{t.credT}</h3>
                  <p className="text-xs text-[#A0A0B0] leading-relaxed">{t.credD}</p>
                </div>
                <div className="sm:w-1/2 bg-[#05050A]/70 border border-white/10 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <img src={ROBOT_B} alt="rank avatar" className="w-8 h-8 rounded-full border border-[#FFD700] shadow-[0_0_14px_rgba(255,215,0,0.55)] bg-[#FFD700]/10 object-cover" />
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-[#FFD700] bg-[#FFD700]/10 border border-[#FFD700]/40 px-2 py-0.5 rounded-full shadow-[0_0_12px_rgba(255,215,0,0.3)]">
                      <Crown size={9} className="fill-[#FFD700]/40" /> {t.credRank}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden mb-3">
                    <motion.div
                      initial={{ width: 0 }} whileInView={{ width: "86%" }} viewport={{ once: true }} transition={{ duration: 1.2 }}
                      className="h-full rounded-full bg-gradient-to-r from-[#FFD700]/60 to-[#FFD700] shadow-[0_0_10px_rgba(255,215,0,0.6)]"
                    />
                  </div>
                  <div className="flex gap-2">
                    {[Trophy, Heart, Rocket].map((Icon, i) => (
                      <span key={i} className="w-8 h-8 rounded-xl flex items-center justify-center text-[#FFD700] border border-[#FFD700]/40 bg-[#FFD700]/10 shadow-[0_0_10px_rgba(255,215,0,0.2)]">
                        <Icon size={13} />
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Monthly prizes */}
            <div className="bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-6 hover:border-[#00FFAA]/30 transition-colors" data-testid="tama-card-prize">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="sm:w-1/2">
                  <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-[#00FFAA] bg-[#00FFAA]/8 border border-[#00FFAA]/30 px-2.5 py-1 rounded-full mb-3">
                    <Gift size={10} /> {t.prizeTag}
                  </span>
                  <h3 className="font-heading font-black text-lg text-white mb-2">{t.prizeT}</h3>
                  <p className="text-xs text-[#A0A0B0] leading-relaxed">{t.prizeD}</p>
                </div>
                <div className="sm:w-1/2 bg-[#05050A]/70 border border-[#FFD700]/25 rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-[inset_0_0_30px_rgba(255,215,0,0.05)]">
                  <motion.span
                    animate={{ y: [0, -5, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-[#FFD700] border border-[#FFD700]/50 bg-[#FFD700]/10 shadow-[0_0_25px_rgba(255,215,0,0.35)] mb-3"
                  >
                    <Trophy size={22} />
                  </motion.span>
                  <p className="font-heading font-black text-sm text-[#FFD700] mb-2">{t.prizeDemo}</p>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-white/50 bg-white/5 border border-white/10 px-3 py-1 rounded-full">
                    ⏳ {daysLeftInMonth()} {t.prizeCountdown}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tamagotchi CTA */}
        <div className="flex justify-center mt-12">
          <button
            data-testid="tama-cta"
            onClick={() => navigate("/register")}
            className="relative inline-flex items-center justify-center gap-2 px-10 py-5 text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 bg-gradient-to-r from-[#FF6EC7] to-[#B026FF] border border-[#FF6EC7]/50 rounded-full shadow-[0_0_25px_rgba(255,110,199,0.5)] hover:shadow-[0_0_45px_rgba(255,110,199,0.8)] hover:-translate-y-1 animate-neon-pulse"
          >
            <Heart size={16} className="fill-white" />
            {t.tamaCta}
          </button>
        </div>
      </div>

      {/* ============ NEURAL LINK SECTION: Talk to your avatar + eternal memory ============ */}
      <div className="relative max-w-7xl mx-auto mt-24 z-30" aria-hidden="true">
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#00FFAA] to-transparent shadow-[0_0_18px_rgba(0,255,170,0.8)]" />
        <div className="absolute left-1/2 -translate-x-1/2 -top-[5px] w-3 h-3 rounded-full bg-[#00FFAA] shadow-[0_0_15px_rgba(0,255,170,1)]" />
      </div>

      <div id="neural-link" className="relative max-w-7xl mx-auto mt-20 z-30" data-testid="neural-hero-section">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#00FFAA] border border-[#00FFAA]/30 bg-[#00FFAA]/5 px-4 py-2 rounded-full mb-6" data-testid="neural-badge">
            <AudioLines size={13} /> {t.neuralBadge}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-white" data-testid="neural-title">
            {t.neuralTitle}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FFAA] via-[#00F5FF] to-[#B026FF]">{t.neuralHighlight}</span>
          </h2>
          <p className="text-base md:text-lg font-light text-[#A0A0B0] mt-4 leading-relaxed">{t.neuralSub}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left: live call mockup */}
          <div className="lg:col-span-5 flex justify-center" data-testid="neural-call-mockup">
            <div className="relative w-full max-w-xs bg-[#0F0F1A]/85 backdrop-blur-2xl border border-[#00FFAA]/25 rounded-[2.5rem] p-7 overflow-hidden shadow-[0_0_45px_rgba(0,255,170,0.15)]">
              <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#00FFAA] blur-[100px] opacity-15 pointer-events-none" />
              <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#00FFAA] flex items-center justify-center gap-2 mb-6">
                <span className="relative flex w-2 h-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FFAA] opacity-75" />
                  <span className="relative inline-flex rounded-full w-2 h-2 bg-[#00FFAA]" />
                </span>
                {t.neuralCallLabel}
              </p>

              {/* Robot with pulsing call rings */}
              <div className="relative flex justify-center mb-5">
                <motion.span
                  animate={{ scale: [1, 1.35, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                  className="absolute w-32 h-32 rounded-full border-2 border-[#00FFAA]/50"
                />
                <motion.span
                  animate={{ scale: [1, 1.6, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 0.5 }}
                  className="absolute w-32 h-32 rounded-full border border-[#00FFAA]/30"
                />
                <motion.img
                  src={HERO_ROBOT}
                  alt="Avatar on call"
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="relative w-28 h-28 object-contain drop-shadow-[0_0_25px_rgba(0,255,170,0.45)]"
                />
              </div>

              {/* Reactive waveform */}
              <div className="flex items-end justify-center gap-1 h-10 mb-5" data-testid="neural-waveform">
                {[14, 26, 38, 22, 34, 18, 40, 24, 30, 16, 36, 20].map((h, i) => (
                  <motion.span
                    key={i}
                    animate={{ height: [h * 0.4, h, h * 0.55, h * 0.9, h * 0.4] }}
                    transition={{ duration: 1.1 + (i % 4) * 0.18, repeat: Infinity, ease: "easeInOut", delay: i * 0.07 }}
                    className="w-1.5 rounded-full bg-gradient-to-t from-[#00FFAA] to-[#00F5FF]"
                    style={{ height: h }}
                  />
                ))}
              </div>

              {/* Avatar speaking bubble */}
              <p className="text-[11px] italic text-white/75 bg-[#00FFAA]/8 border border-[#00FFAA]/25 rounded-2xl rounded-bl-sm px-4 py-3 mb-6 leading-relaxed">
                {matchmakingOn ? t.neuralDemoLine : t.neuralDemoLineTama}
              </p>

              {/* Call controls */}
              <div className="flex items-center justify-center gap-5">
                <span className="w-11 h-11 rounded-full flex items-center justify-center bg-white/5 border border-white/15 text-white/70">
                  <Mic size={17} />
                </span>
                <span className="w-14 h-14 rounded-full flex items-center justify-center bg-[#00FFAA] text-[#05050A] shadow-[0_0_25px_rgba(0,255,170,0.6)]">
                  <Phone size={22} className="fill-[#05050A]" />
                </span>
                <span className="w-11 h-11 rounded-full flex items-center justify-center bg-[#FF4444]/15 border border-[#FF4444]/40 text-[#FF4444]">
                  <PhoneOff size={17} />
                </span>
              </div>
            </div>
          </div>

          {/* Right: features + memory chips */}
          <div className="lg:col-span-7">
            <div className="space-y-5 mb-7">
              {[
                { icon: AudioLines, title: t.neuralF1t, text: t.neuralF1, color: "#00FFAA" },
                { icon: BrainCircuit, title: t.neuralF2t, text: t.neuralF2, color: "#00F5FF" },
                { icon: MessageCircle, title: t.neuralF3t, text: matchmakingOn ? t.neuralF3 : t.neuralF3Tama, color: "#B026FF" },
              ].map(({ icon: Icon, title, text, color }) => (
                <div key={title} className="flex items-start gap-4 bg-[#0F0F1A]/70 border border-white/10 rounded-3xl p-5 hover:border-white/25 transition-colors">
                  <span
                    className="w-11 h-11 rounded-2xl flex items-center justify-center border shrink-0"
                    style={{ color, borderColor: `${color}40`, backgroundColor: `${color}10`, boxShadow: `0 0 18px ${color}25` }}
                  >
                    <Icon size={19} />
                  </span>
                  <div>
                    <h3 className="font-heading font-black text-sm text-white mb-1">{title}</h3>
                    <p className="text-xs text-[#A0A0B0] leading-relaxed">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Memory chips */}
            <div className="bg-[#05050A]/70 border border-[#00F5FF]/20 rounded-3xl p-5 mb-8" data-testid="neural-memory-chips">
              <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#00F5FF] mb-3 flex items-center gap-2">
                <BrainCircuit size={12} /> {t.neuralMemLabel}
              </p>
              <div className="flex flex-wrap gap-2">
                {t.neuralMems.map((m, i) => (
                  <motion.span
                    key={m}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.2 }}
                    className="text-[11px] font-bold text-white/80 bg-[#00F5FF]/8 border border-[#00F5FF]/30 px-3.5 py-2 rounded-full"
                  >
                    {m} ✓
                  </motion.span>
                ))}
              </div>
            </div>

            <button
              data-testid="neural-cta"
              onClick={() => navigate("/register")}
              className="inline-flex items-center justify-center gap-2 px-9 py-4 text-sm font-bold uppercase tracking-widest text-[#05050A] transition-all duration-300 bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] rounded-full shadow-[0_0_28px_rgba(0,255,170,0.5)] hover:shadow-[0_0_45px_rgba(0,255,170,0.8)] hover:-translate-y-1"
            >
              <Phone size={16} className="fill-[#05050A]" />
              {t.neuralCta}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
